import moment from 'moment/min/moment-with-locales';
import momentTZ from 'moment-timezone';

export const DEFAULT_TIMEZONE = 'Europe/Tallinn';

export const DATE_FORMATS = {
  DD_MMMM: 'DD MMMM',
  MMMM_YYYY: 'MMMM YYYY',
  D_MMMM: 'D MMMM',
  D: 'D',
  M: 'M',
  DD_MMM: 'DD MMM.',
  DD_MMMM_YYYY: 'DD MMMM, YYYY',
  MMMM_DD_YYYY: 'MMMM DD YYYY',
  DD_MM_YYYY: 'DD.MM.YYYY',
  MMM_YYYY: 'MMM YYYY',
  DD_MMM_YYYY: 'DD MMM YYYY',
  MM_YYYY: 'MM/YYYY',
  MMM_DO_YYYY: 'MMM Do, YYYY',
  YYYY: 'YYYY',
  REQUEST: 'YYYY-MM-DD',
} as const;

export type DateFormat = (typeof DATE_FORMATS)[keyof typeof DATE_FORMATS];

interface DateRangeParams {
  initialDate?: string | Date;
  targetDate: string;
  targetTimezone?: string;
}

function resolveDates({ initialDate, targetDate, targetTimezone = DEFAULT_TIMEZONE }: DateRangeParams) {
  const isDateOnly = momentTZ(targetDate, DATE_FORMATS.REQUEST, true).isValid();

  const target = isDateOnly
    ? momentTZ.tz(targetDate, targetTimezone)
    : momentTZ(targetDate); // ISO with embedded offset — timezone is taken from the string itself

  if (!target.isValid()) {
    throw new Error(`Invalid targetDate: "${targetDate}"`);
  }

  const initial = initialDate ? momentTZ(initialDate) : momentTZ();

  return { initial, target };
}

export const prettyDate = (date: DateFormat, format = DATE_FORMATS.DD_MM_YYYY, locale?: string) =>
  date ? moment(date).locale(locale ?? 'en').format(format) : '';

export const isBefore = (params: DateRangeParams): boolean => {
  const { initial, target } = resolveDates(params);
  return initial.isBefore(target);
};

export const isAfter = (params: DateRangeParams): boolean => {
  const { initial, target } = resolveDates(params);
  return initial.isAfter(target);
};